import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Home, MapPin, Calendar, DollarSign, Bed, Bath, Layers,
    ArrowLeft, ArrowRight, Shield, Zap, Maximize, Landmark,
    Edit2, Loader2, Plus, RefreshCw, PlugZap, Building2
} from 'lucide-react';
import HouseService from '../../services/HouseService';
import UtilityService from '../../services/UtilityService';
import HouseImageManager from '../../components/v2/HouseDetails/HouseImageManager';
import { Card, Button, Input, Select, toast, V2Tabs, LoadingState, ErrorState, EmptyState } from '../../components/v2/V2Components';
import UtilityCard from '../../components/v2/UtilityCard';
import InsuranceTab from '../../components/v2/InsuranceTab';
import FloorPlansSection from '../../components/v2/HouseFloorPlans/FloorPlansSection';
import { isSafeHttpUrl } from '../../utils/validation';
import MortgageTab from '../../components/v2/MortgageTab';
import ElectricalTab from '../../components/v2/ElectricalTab';
import StructuresTab from '../../components/v2/StructuresTab';
import { UTILITY_TYPES, getUtilityMeta } from '../../utils/utilityIcons';
import usePageView from '../../hooks/usePageView';
import analytics from '../../services/AnalyticsService';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const VALID_TABS = ['overview', 'rooms', 'insurance', 'utilities', 'mortgage', 'electrical', 'structures'];

const tabFromUrl = (sp) => {
    const t = sp.get('tab');
    return VALID_TABS.includes(t) ? t : 'overview';
};

const hasDisplayValue = (value) => value !== null && value !== undefined && value !== '';

// Provenance labels for tax rows. Anything that isn't "rentcast" is county
// (authoritative) data and gets the primary-colored badge.
const SOURCE_LABELS = { cuyahoga: 'County', rentcast: 'RentCast' };

const SourceBadge = ({ source }) => {
    if (!source) return null;
    const label = SOURCE_LABELS[source] || source;
    const isCounty = source !== 'rentcast';
    return (
        <span
            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-[var(--border)] ${
                isCounty ? 'text-[var(--primary)]' : 'text-[var(--text-secondary)]'
            }`}
        >
            {label}
        </span>
    );
};

const DetailItem = ({ label, value, highlight = false }) => (
    <div className="flex justify-between items-center py-2 border-b border-[var(--border)] last:border-0">
        <span className="text-sm text-[var(--text-secondary)]">{label}</span>
        <span className={`text-sm font-medium ${highlight ? 'text-[var(--primary)]' : 'text-[var(--text-main)]'}`}>
            {hasDisplayValue(value) ? value : 'N/A'}
        </span>
    </div>
);

const HouseDetails = () => {
    usePageView(ANALYTICS_EVENTS.HOUSES_DETAILS_VIEWED);
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [house, setHouse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // HTTP status of the load failure — gates the Retry affordance on the
    // status code (404 = permanent, no retry) instead of string-matching
    // the display copy.
    const [errorCode, setErrorCode] = useState(null);
    const [activeTab, setActiveTab] = useState(() => tabFromUrl(searchParams));
    const [refreshingTax, setRefreshingTax] = useState(false);

    // Keep activeTab reactive to URL changes (browser back/forward, programmatic nav).
    useEffect(() => {
        const next = tabFromUrl(searchParams);
        setActiveTab((prev) => (prev === next ? prev : next));
    }, [searchParams]);

    // Utilities state
    const [utilities, setUtilities] = useState([]);
    const [utilitiesLoading, setUtilitiesLoading] = useState(false);
    // Inline error for the Utilities tab — a toast alone leaves the panel
    // looking empty after a failed load, with no way to retry.
    const [utilitiesError, setUtilitiesError] = useState(null);
    const [utilitiesLoaded, setUtilitiesLoaded] = useState(false);
    const [showAddUtility, setShowAddUtility] = useState(false);
    const [newUtilityType, setNewUtilityType] = useState('electric');
    const [newUtilityName, setNewUtilityName] = useState('');
    const [newUtilityUrl, setNewUtilityUrl] = useState('');
    const [newUtilityNotes, setNewUtilityNotes] = useState('');
    const [creatingUtility, setCreatingUtility] = useState(false);

    useEffect(() => {
        const houseId = parseInt(id);
        if (isNaN(houseId) || houseId <= 0) {
            setError('Invalid house ID');
            setLoading(false);
            return;
        }
        fetchHouseDetails();
        // fetchHouseDetails closes over `id` via parseInt(id) — re-running only
        // on id change is the intended behavior; including the fetch in deps
        // would either loop or require a useCallback wrap that adds no value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchUtilities = async () => {
        const houseId = parseInt(id);
        if (isNaN(houseId) || houseId <= 0) return;
        try {
            setUtilitiesLoading(true);
            setUtilitiesError(null);
            const data = await UtilityService.getUtilities(houseId);
            setUtilities(data.utilities || []);
            setUtilitiesLoaded(true);
        } catch (err) {
            setUtilitiesError(err.message || 'Failed to load utilities');
        } finally {
            setUtilitiesLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'utilities' && !utilitiesLoaded) {
            fetchUtilities();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleCreateUtility = async (e) => {
        e.preventDefault();
        if (newUtilityType === 'other' && !newUtilityName.trim()) {
            toast.error?.('Name is required when type is "other"');
            return;
        }
        // Mirrors UtilityDetails handleSaveMeta: validate against the same
        // isSafeHttpUrl helper UtilityCard's render guard uses, so a value
        // saved here is one the card will actually render as a link.
        if (newUtilityUrl && !isSafeHttpUrl(newUtilityUrl)) {
            toast.error?.('Account URL must be a valid http:// or https:// link');
            return;
        }
        try {
            setCreatingUtility(true);
            const created = await UtilityService.createUtility(parseInt(id), {
                type: newUtilityType,
                name: newUtilityName.trim() || null,
                account_url: newUtilityUrl || null,
                notes: newUtilityNotes || null,
            });
            setUtilities((prev) => [...prev, created]);
            setShowAddUtility(false);
            setNewUtilityType('electric');
            setNewUtilityName('');
            setNewUtilityUrl('');
            setNewUtilityNotes('');
            toast.success?.('Utility added');
        } catch (err) {
            toast.error?.(err.message || 'Failed to add utility');
        } finally {
            setCreatingUtility(false);
        }
    };

    const handleSelectTab = (key) => {
        setActiveTab(key);
        analytics.track(ANALYTICS_EVENTS.HOUSES_DETAILS_TAB_CHANGED, {
            tab_key: key,
        });
        if (key === 'overview') {
            // Clear ?tab on overview to keep URL clean.
            const next = new URLSearchParams(searchParams);
            next.delete('tab');
            setSearchParams(next, { replace: true });
        } else {
            const next = new URLSearchParams(searchParams);
            next.set('tab', key);
            setSearchParams(next, { replace: true });
        }
    };

    const fetchHouseDetails = async () => {
        try {
            const data = await HouseService.getHouse(id);
            setHouse(data);
            setError(null);
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error.code ?? null);
            if (error.code === 404) {
                setError('House not found.');
            } else if (error.code === 401) {
                setError('Authentication required.');
            } else {
                setError(error.message || 'Failed to load details.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshCountyTax = async () => {
        try {
            setRefreshingTax(true);
            const result = await HouseService.refreshCountyTax(id);
            if (result?.house) setHouse(result.house);
            if (result?.updated) {
                toast.success?.('Tax history updated from county records');
            } else {
                toast.info?.('No county tax records available for this property');
            }
        } catch (err) {
            toast.error?.(err.message || 'Failed to refresh county tax');
        } finally {
            setRefreshingTax(false);
        }
    };

    const formatCurrency = (value) => {
        if (value == null || value === '') return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatDate = (dateString) => {
        if (dateString == null || dateString === '') return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Gallery photos only — floor plans are HouseImage rows too
    // (is_floor_plan=true) but belong to the Floor Plans section, not the
    // hero's photo manager.
    const galleryImages = (house?.images || []).filter((img) => !img.is_floor_plan);

    if (loading) return <LoadingState fullPage />;

    if (error || !house) return (
        <ErrorState
            title="Error Loading Property"
            message={error || 'Property not found'}
            onRetry={errorCode !== 404
                ? () => { setLoading(true); setError(null); fetchHouseDetails(); }
                : undefined}
            action={{ label: 'Back to Properties', onClick: () => navigate('/houses') }}
        />
    );

    return (
        <div className="animate-fade-in max-w-6xl mx-auto space-y-6 pb-10">
            {/* Header / Nav */}
            <button
                onClick={() => navigate('/houses')}
                className="flex items-center text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors mb-2"
            >
                <ArrowLeft size={18} className="mr-1" />
                <span>Back to Properties</span>
            </button>

            {/* Hero Section — the house's single photo gallery beside its key
                facts. The split keys off the card's own width (@container), not
                the viewport: the sidebar appears at lg and narrows the card, so
                a viewport breakpoint would squeeze the facts column at
                1024–1215px. */}
            <Card className="overflow-hidden @container">
                <div className="grid grid-cols-1 @4xl:grid-cols-5">
                    {/* Photos — the page's only photo surface: carousel,
                        thumbnails, upload (incl. mobile camera), set-primary,
                        delete. Renders house.images (incl. the Street View
                        image saved at create) via their presigned URLs. */}
                    <div className="@4xl:col-span-3 p-4 @lg:p-6">
                        {/* 4:3 on phone-width cards keeps the photo from
                            collapsing to a letterbox strip; 16:10 once there's room. */}
                        <HouseImageManager
                            houseId={house.id}
                            images={galleryImages}
                            onChange={fetchHouseDetails}
                            aspectRatio="aspect-[4/3] @lg:aspect-[16/10]"
                        />
                    </div>

                    {/* Details — name, address, Edit, key stats. */}
                    <div className="@4xl:col-span-2 flex flex-col px-4 pb-6 @lg:px-6 @4xl:py-6 @4xl:pl-0">
                        <div className="mb-8">
                            <div className="flex justify-between items-start gap-4">
                                <div className="min-w-0">
                                    {house.is_primary && (
                                        <span className="inline-block mb-2 bg-[var(--primary)] text-white text-xs font-bold px-3 py-1 rounded shadow-sm">
                                            Primary Residence
                                        </span>
                                    )}
                                    <h1 className="text-2xl @lg:text-3xl font-bold text-[var(--text-main)] mb-2 break-words">{house.name}</h1>
                                    {house.address && (
                                        <div className="flex items-start text-[var(--text-secondary)]">
                                            <MapPin size={18} className="mr-2 mt-0.5 flex-shrink-0" />
                                            <span>
                                                {house.address.street}
                                                {house.address.unit && `, Unit ${house.address.unit}`}
                                                <br />
                                                {house.address.city && `${house.address.city}, `}
                                                {house.address.state} {house.address.zip_code}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        // No props: the event name already conveys the
                                        // intent. is_edit_mode would be constant here AND
                                        // means form-state ("in edit mode") on form events
                                        // — a different semantic. Keep it off this event.
                                        analytics.track(ANALYTICS_EVENTS.HOUSES_DETAILS_EDIT_CLICKED, {});
                                        navigate(`/houses/${id}/edit`);
                                    }}
                                    className="flex flex-shrink-0 items-center space-x-2 px-4 py-2 bg-[var(--bg-hover)] border border-[var(--border)] rounded-lg hover:border-[var(--primary)] transition-all"
                                >
                                    <Edit2 size={16} />
                                    <span>Edit</span>
                                </button>
                            </div>
                        </div>

                        {/* Key Stats — always 2×2. Beside the photos, flex-1 +
                            grid-rows-2 stretch the tiles to fill the column so
                            they end level with the thumbnail strip; stacked,
                            there's no spare height and they keep natural size.
                            The mb-8 above is the floor gap either way. */}
                        <div data-testid="house-key-stats" className="grid grid-cols-2 grid-rows-2 gap-3 @lg:gap-4 flex-1">
                            <div className="flex flex-col items-center justify-center text-center p-3 bg-[var(--bg-hover)] rounded-xl">
                                <Bed size={24} className="text-[var(--primary)] mb-2 mx-auto" />
                                <div className="font-bold text-[var(--text-main)] @4xl:text-2xl">{house.bedrooms ?? '-'}</div>
                                <div className="text-xs text-[var(--text-muted)] uppercase">Bedrooms</div>
                            </div>
                            <div className="flex flex-col items-center justify-center text-center p-3 bg-[var(--bg-hover)] rounded-xl">
                                <Bath size={24} className="text-[var(--primary)] mb-2 mx-auto" />
                                <div className="font-bold text-[var(--text-main)] @4xl:text-2xl">{house.bathrooms ?? '-'}</div>
                                <div className="text-xs text-[var(--text-muted)] uppercase">Bathrooms</div>
                            </div>
                            <div className="flex flex-col items-center justify-center text-center p-3 bg-[var(--bg-hover)] rounded-xl">
                                <Maximize size={24} className="text-[var(--primary)] mb-2 mx-auto" />
                                <div className="font-bold text-[var(--text-main)] @4xl:text-2xl">{house.square_footage ? house.square_footage.toLocaleString() : '-'}</div>
                                <div className="text-xs text-[var(--text-muted)] uppercase">Sq Ft</div>
                            </div>
                            <div className="flex flex-col items-center justify-center text-center p-3 bg-[var(--bg-hover)] rounded-xl">
                                <Calendar size={24} className="text-[var(--primary)] mb-2 mx-auto" />
                                <div className="font-bold text-[var(--text-main)] @4xl:text-2xl">{house.year_built || '-'}</div>
                                <div className="text-xs text-[var(--text-muted)] uppercase">Year Built</div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Tabs & Content */}
            <div>
                <V2Tabs
                    activeTab={activeTab}
                    onSelect={handleSelectTab}
                    tabs={[
                        { key: 'overview', label: 'Overview', icon: Home },
                        { key: 'rooms', label: `Rooms (${house.rooms?.length || 0})`, icon: Layers },
                        { key: 'insurance', label: 'Insurance', icon: Shield },
                        { key: 'utilities', label: 'Utilities', icon: Zap },
                        { key: 'mortgage', label: 'Mortgage', icon: Landmark },
                        { key: 'electrical', label: 'Electrical', icon: PlugZap },
                        { key: 'structures', label: 'Structures', icon: Building2 },
                    ]}
                    className="mb-6"
                />

                {activeTab === 'overview' && (
                    <div className="animate-fade-in space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="p-6">
                            <h3 className="text-lg font-bold text-[var(--text-main)] mb-4 flex items-center">
                                <Home size={20} className="mr-2 text-[var(--primary)]" />
                                Property Details
                            </h3>
                            <div className="space-y-1">
                                <DetailItem label="Property Type" value={house.property_type} />
                                <DetailItem label="Floors" value={house.floor_count} />
                                <DetailItem label="Lot Size" value={house.lot_size_sqft ? `${Number(house.lot_size_sqft).toLocaleString()} sq ft` : null} />
                                <DetailItem label="Architecture" value={house.architecture_type} />
                                <DetailItem label="Exterior" value={house.exterior_type} />
                                <DetailItem label="Heating" value={house.heating_type} />
                                <DetailItem label="Roof Type" value={house.roof_type} />
                                {/* "Parking", not "Garage": the type can now be a spot, street
                                    parking, or None. garage_type is the stored answer; the
                                    has_garage fallback only covers legacy rows that have the
                                    boolean but never got a type. */}
                                <DetailItem label="Parking" value={house.garage_type || (house.has_garage ? 'Yes' : 'No')} />
                                {/* hasDisplayValue, not truthiness: garage_spaces permits 0
                                    (ge=0 in the schema, and RentCast passes garageSpaces
                                    straight through), and a bare `?` would hide a real
                                    zero. The backend clears whichever of these the type
                                    disallows, so at most one ever renders. */}
                                {hasDisplayValue(house.garage_spaces) ? (
                                    <DetailItem label="Garage Size" value={`${house.garage_spaces} car`} />
                                ) : null}
                                {hasDisplayValue(house.parking_spot) ? (
                                    <DetailItem label="Spot / Space #" value={house.parking_spot} />
                                ) : null}
                            </div>
                        </Card>

                        <Card className="p-6">
                            <h3 className="text-lg font-bold text-[var(--text-main)] mb-4 flex items-center">
                                <DollarSign size={20} className="mr-2 text-green-600" />
                                Financial Information
                            </h3>
                            <div className="space-y-1">
                                <DetailItem label="Purchase Price" value={formatCurrency(house.purchase_price)} />
                                <DetailItem label="Purchase Date" value={formatDate(house.purchase_date)} />
                                <DetailItem
                                    label="Estimated Value"
                                    value={formatCurrency(house.current_estimated_value)}
                                    highlight
                                />
                                <DetailItem label={`Property Tax (${house.property_tax_year || 'Latest'})`} value={formatCurrency(house.last_property_tax_amount)} />
                                <DetailItem label="Annual Maint. Budget" value={formatCurrency(house.annual_maintenance_budget)} />
                                <DetailItem label="Improvement Budget" value={formatCurrency(house.annual_improvement_budget)} />
                            </div>
                        </Card>
                    </div>

                    <Card className="p-6">
                        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                            <h3 className="text-lg font-bold text-[var(--text-main)] flex items-center">
                                <Landmark size={20} className="mr-2 text-[var(--primary)]" />
                                Property Tax History
                            </h3>
                            <Button
                                variant="secondary"
                                onClick={handleRefreshCountyTax}
                                loading={refreshingTax}
                                leadingIcon={RefreshCw}
                                className="flex items-center gap-2"
                            >
                                Refresh from county
                            </Button>
                        </div>

                        {(house.tax_records?.length > 0 || house.tax_assessments?.length > 0) ? (
                            <div className="space-y-6">
                                {house.tax_records?.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-[var(--text-secondary)] mb-2">Annual Taxes</h4>
                                        <div className="space-y-1">
                                            {house.tax_records.map((rec) => (
                                                <div key={`tax-${rec.year}`} className="flex justify-between items-center py-2 border-b border-[var(--border)] last:border-0">
                                                    <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
                                                        {rec.year}
                                                        <SourceBadge source={rec.source} />
                                                    </span>
                                                    <span className="text-sm font-medium text-[var(--text-main)]">
                                                        {formatCurrency(rec.total_amount)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {house.tax_assessments?.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-[var(--text-secondary)] mb-2">Assessed Values</h4>
                                        <div className="space-y-1">
                                            {house.tax_assessments.map((a) => (
                                                <div key={`assess-${a.year}`} className="flex justify-between items-center py-2 border-b border-[var(--border)] last:border-0">
                                                    <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
                                                        {a.year}
                                                        <SourceBadge source={a.source} />
                                                    </span>
                                                    <span className="text-sm font-medium text-[var(--text-main)]">
                                                        {formatCurrency(a.total_value)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-[var(--text-secondary)]">
                                No tax history on file yet. Use “Refresh from county” to pull records for a supported
                                county (currently Cuyahoga County, OH), or add the property via address auto-fill.
                            </p>
                        )}
                    </Card>
                    </div>
                )}

                {activeTab === 'rooms' && (
                    <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
                        {house.rooms && house.rooms.length > 0 ? (
                            house.rooms.map((room) => (
                                <Card
                                    key={room.id}
                                    className="p-5 hover:border-[var(--primary)] cursor-pointer group transition-all"
                                    onClick={() => {
                                        analytics.track(ANALYTICS_EVENTS.HOUSES_DETAILS_ROOM_CLICKED, {});
                                        navigate(`/room/${room.id}`);
                                    }}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-bold text-lg text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors">
                                            {room.name}
                                        </h4>
                                        <ArrowRight size={18} className="text-[var(--text-muted)] group-hover:text-[var(--primary)] group-hover:translate-x-1 transition-all" />
                                    </div>
                                    <p className="text-sm text-[var(--text-secondary)]">
                                        {room.floor && `${room.floor}`}
                                        {room.floor && room.items_count !== undefined && ' • '}
                                        {room.items_count !== undefined && `${room.items_count} items`}
                                    </p>
                                </Card>
                            ))
                        ) : (
                            <div className="col-span-full py-12 text-center text-[var(--text-muted)] border-2 border-dashed border-[var(--border)] rounded-xl">
                                <Layers size={48} className="mx-auto mb-3 opacity-30" />
                                <p>No rooms added yet</p>
                            </div>
                        )}
                    </div>
                    {/* Assembled per-floor plans (House Floor Plans, ADR 0007) */}
                    <FloorPlansSection house={house} />
                    </>
                )}

                {activeTab === 'insurance' && (
                    <div className="animate-fade-in">
                        <InsuranceTab houseId={id} />
                    </div>
                )}

                {activeTab === 'utilities' && (
                    <div className="animate-fade-in">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
                            <div>
                                <h3 className="text-lg font-bold text-[var(--text-main)]">Utilities</h3>
                                <p className="text-xs text-[var(--text-secondary)] mt-1">
                                    Track accounts, store bills, and view rolling 12-month averages.
                                </p>
                            </div>
                            {!showAddUtility && (
                                <Button leadingIcon={Plus} onClick={() => setShowAddUtility(true)}>
                                    Add Utility
                                </Button>
                            )}
                        </div>

                        {showAddUtility && (
                            <Card className="p-5 mb-4 border border-[var(--border)]">
                                <h4 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide mb-3">
                                    New Utility
                                </h4>
                                <form onSubmit={handleCreateUtility} className="space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
                                                Type
                                            </label>
                                            <Select
                                                value={newUtilityType}
                                                onChange={(e) => setNewUtilityType(e.target.value)}
                                            >
                                                {UTILITY_TYPES.map((t) => (
                                                    <option key={t} value={t}>
                                                        {getUtilityMeta(t).label}
                                                    </option>
                                                ))}
                                            </Select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
                                                Name {newUtilityType === 'other' && <span className="text-red-500">*</span>}
                                            </label>
                                            <Input
                                                type="text"
                                                value={newUtilityName}
                                                onChange={(e) => setNewUtilityName(e.target.value)}
                                                placeholder={newUtilityType === 'other' ? 'Required' : 'e.g. ConEd'}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
                                            Account URL
                                        </label>
                                        <Input
                                            type="url"
                                            value={newUtilityUrl}
                                            onChange={(e) => setNewUtilityUrl(e.target.value)}
                                            placeholder="https://provider.example.com/account"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
                                            Notes
                                        </label>
                                        <Input
                                            type="text"
                                            value={newUtilityNotes}
                                            onChange={(e) => setNewUtilityNotes(e.target.value)}
                                            placeholder="Optional"
                                        />
                                    </div>
                                    <div className="flex justify-end gap-2 pt-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => setShowAddUtility(false)}
                                            disabled={creatingUtility}
                                        >
                                            Cancel
                                        </Button>
                                        <Button type="submit" loading={creatingUtility}>
                                            Add Utility
                                        </Button>
                                    </div>
                                </form>
                            </Card>
                        )}

                        {utilitiesLoading ? (
                            <Card className="p-10 flex items-center justify-center text-[var(--text-muted)]">
                                <Loader2 className="animate-spin mr-2" size={18} />
                                Loading utilities…
                            </Card>
                        ) : utilitiesError ? (
                            <Card>
                                <ErrorState
                                    title="Couldn't load utilities"
                                    message={utilitiesError}
                                    onRetry={fetchUtilities}
                                    className="p-6"
                                />
                            </Card>
                        ) : utilities.length === 0 ? (
                            !showAddUtility && (
                                <Card>
                                    <EmptyState
                                        icon={Zap}
                                        title="No utilities tracked yet"
                                        message="Track providers, account links, and monthly costs for this property."
                                        actionLabel="Add your first utility"
                                        onAction={() => setShowAddUtility(true)}
                                        className="py-6"
                                    />
                                </Card>
                            )
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {utilities.map((u) => (
                                    <UtilityCard
                                        key={u.id}
                                        utility={u}
                                        onClick={() => {
                                            analytics.track(ANALYTICS_EVENTS.HOUSES_DETAILS_UTILITY_CLICKED, {});
                                            navigate(`/houses/${id}/utilities/${u.id}`);
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'mortgage' && (
                    <div className="animate-fade-in">
                        <MortgageTab houseId={id} />
                    </div>
                )}

                {activeTab === 'electrical' && (
                    <div className="animate-fade-in">
                        <ElectricalTab houseId={id} />
                    </div>
                )}

                {activeTab === 'structures' && (
                    <div className="animate-fade-in">
                        <StructuresTab houseId={id} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default HouseDetails;
